'use client'

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  HiCheck,
  HiChevronDown,
  HiMagnifyingGlass,
  HiMiniBars3BottomLeft,
  HiMiniHashtag,
  HiOutlineAdjustmentsHorizontal,
  HiOutlineClock,
  HiOutlineFunnel,
  HiXMark,
} from 'react-icons/hi2'
import { LuDollarSign } from 'react-icons/lu'
import { twMerge } from 'tailwind-merge'

import Button from '@/components/common/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/common/DropdownMenu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/Select'
import Spinner from '@/components/common/Spinner'
import RecipientExportButton from '@/components/recipient-search/RecipientExportButton'

import {
  PREFIX_TRIGGERS,
  detectIdentifierPattern,
  detectPrefixKeyword,
} from '@/lib/recipient-search/identifierDetection'

import { useRecipientSearchStore } from '@/stores/RecipientSearchStore'

import {
  IDENTIFIER_COLORS,
  type IdentifierType,
  type RecipientSortOption,
  SORT_OPTIONS,
  type SortIconType,
} from '@/types/recipient-search'

// =============================================================================
// HOISTED CONSTANTS (rendering-hoist-jsx, js-hoist-regexp)
// =============================================================================

const PLACEHOLDER_TEXT: Record<IdentifierType | 'default', string> = {
  uei: 'e.g. J7M9HPTGJ1S9',
  duns: 'e.g. 123456789',
  cage: 'e.g. 1ABC2',
  default: 'Search contractors...',
}

const SORT_ICONS: Record<
  SortIconType,
  React.ComponentType<{ className?: string }>
> = {
  dollar: LuDollarSign,
  hash: HiMiniHashtag,
  clock: HiOutlineClock,
  text: HiMiniBars3BottomLeft,
}

// Pure function - no component dependencies
const getIdentifierColors = (type: IdentifierType): string => {
  const colors = IDENTIFIER_COLORS[type]
  return `${colors.bg} ${colors.text} ${colors.border} ${colors.darkBg} ${colors.darkText} ${colors.darkBorder}`
}

// =============================================================================
// MEMOIZED SUBCOMPONENTS (rerender-memo)
// =============================================================================

// Dropdown content - compact design
const SearchTypeDropdownContent = memo(function SearchTypeDropdownContent({
  identifierType,
  onSelect,
}: {
  identifierType: IdentifierType | null
  onSelect: (type: IdentifierType | null) => void
}) {
  return (
    <DropdownMenuContent align="start" className="w-48 py-1">
      {/* Name search option */}
      <DropdownMenuItem
        onClick={() => onSelect(null)}
        className="flex items-center gap-2 py-1.5 px-2 cursor-pointer text-sm"
      >
        <div className="w-4 flex justify-center">
          {identifierType === null ? (
            <HiCheck className="w-3.5 h-3.5 text-accent-500" />
          ) : null}
        </div>
        <HiMagnifyingGlass className="w-3.5 h-3.5 text-primary-400 dark:text-primary-500" />
        <span className="text-primary-900 dark:text-primary-100">Name</span>
      </DropdownMenuItem>

      <DropdownMenuSeparator className="my-1" />

      {/* Identifier options */}
      {PREFIX_TRIGGERS.map((trigger) => (
        <DropdownMenuItem
          key={trigger.type}
          onClick={() => onSelect(trigger.type)}
          className="flex items-center gap-2 py-1.5 px-2 cursor-pointer text-sm"
        >
          <div className="w-4 flex justify-center">
            {identifierType === trigger.type ? (
              <HiCheck className="w-3.5 h-3.5 text-accent-500" />
            ) : null}
          </div>
          <span
            className={twMerge(
              'w-10 text-center px-1 py-0.5 rounded text-[9px] font-semibold uppercase border',
              getIdentifierColors(trigger.type),
            )}
          >
            {trigger.type}
          </span>
          <span className="text-primary-600 dark:text-primary-300 font-mono text-xs">
            {trigger.example}
          </span>
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  )
})

// =============================================================================
// MAIN COMPONENT
// =============================================================================

interface RecipientToolbarProps {
  totalCount?: number
  isLoading?: boolean
  hasExactMatch?: boolean
  className?: string
}

const RecipientToolbar: React.FC<RecipientToolbarProps> = ({
  totalCount,
  isLoading = false,
  hasExactMatch = false,
  className,
}) => {
  const {
    query,
    filters,
    identifierType,
    isSearching,
    setQuery,
    setDisplayQuery,
    setSort,
    setIdentifierType,
    clearIdentifierType,
    toggleFilterPanel,
    filterPanelOpen,
    getActiveFilterCount,
  } = useRecipientSearchStore()

  const inputRef = useRef<HTMLInputElement>(null)
  const [inputValue, setInputValue] = useState(query)
  const [isFocused, setIsFocused] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)

  // Sync input value with store
  useEffect(() => {
    setInputValue(query)
  }, [query])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputValue !== query) {
        setQuery(inputValue)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [inputValue, query, setQuery])

  // Keyboard shortcut: Cmd/Ctrl + K to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Detect exact pattern match or prefix keyword (rerender-derived-state-no-effect)
  // Combined into single memo to avoid duplicate detectPrefixKeyword calls
  const { detectedType, detectedFromPrefix } = useMemo(() => {
    if (identifierType) return { detectedType: null, detectedFromPrefix: false }
    // First check for exact pattern match (e.g., "1ABC2" → CAGE)
    const patternMatch = detectIdentifierPattern(inputValue)
    if (patternMatch)
      return { detectedType: patternMatch, detectedFromPrefix: false }
    // Then check for prefix keyword (e.g., "duns" → DUNS)
    const prefixMatch = detectPrefixKeyword(inputValue)
    return {
      detectedType: prefixMatch,
      detectedFromPrefix: prefixMatch !== null,
    }
  }, [inputValue, identifierType])

  // Stable callbacks (rerender-functional-setstate)
  const handleInputChange = useCallback(
    (value: string) => {
      // Check for prefix triggers
      for (const trigger of PREFIX_TRIGGERS) {
        if (value.toLowerCase().startsWith(trigger.prefix)) {
          const remainder = value.slice(trigger.prefix.length)
          setIdentifierType(trigger.type)
          setInputValue(remainder)
          setDisplayQuery(remainder)
          return
        }
      }
      setInputValue(value)
      setDisplayQuery(value)
    },
    [setIdentifierType, setDisplayQuery],
  )

  const handleSwitchToDetectedType = useCallback(() => {
    if (detectedType) {
      setIdentifierType(detectedType)
      // If detected from typing a prefix keyword (e.g., "duns"), clear the input
      if (detectedFromPrefix) {
        setInputValue('')
        setDisplayQuery('')
      }
      inputRef.current?.focus()
    }
  }, [detectedType, detectedFromPrefix, setIdentifierType, setDisplayQuery])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && inputValue === '' && identifierType) {
        e.preventDefault()
        clearIdentifierType()
      }
      // Tab accepts the detected identifier type hint
      if (e.key === 'Tab' && detectedType) {
        e.preventDefault()
        handleSwitchToDetectedType()
      }
    },
    [
      inputValue,
      identifierType,
      clearIdentifierType,
      detectedType,
      handleSwitchToDetectedType,
    ],
  )

  const handleClear = useCallback(() => {
    if (identifierType) {
      clearIdentifierType()
    } else {
      setInputValue('')
      setQuery('')
      setDisplayQuery('')
    }
    inputRef.current?.focus()
  }, [identifierType, clearIdentifierType, setQuery, setDisplayQuery])

  const handleTypeSelect = useCallback(
    (type: IdentifierType | null) => {
      if (type === null) {
        clearIdentifierType()
      } else {
        setIdentifierType(type)
      }
      setDropdownOpen(false)
      inputRef.current?.focus()
    },
    [setIdentifierType, clearIdentifierType],
  )

  const handleSortChange = useCallback(
    (value: string) => {
      setSort(value as RecipientSortOption)
    },
    [setSort],
  )

  const activeFilterCount = getActiveFilterCount()
  const placeholder = identifierType
    ? PLACEHOLDER_TEXT[identifierType]
    : PLACEHOLDER_TEXT.default

  // Show hint when empty to signal dropdown availability
  const showEmptyHint = !inputValue && !identifierType

  return (
    <div
      className={twMerge(
        'flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-between gap-3 px-4 py-3',
        'bg-white dark:bg-primary-900',
        'border-b border-primary-200/80 dark:border-primary-700/80',
        className,
      )}
    >
      {/* Search Input with Type Selector */}
      <div
        className={twMerge(
          'relative w-full sm:flex-1 sm:max-w-2xl transition-all duration-200',
          isFocused ? 'sm:max-w-3xl' : '',
        )}
      >
        {/* Left area: icon/badge + dropdown trigger */}
        <div className="absolute inset-y-0 left-0 z-10 flex items-center pl-3 gap-0.5">
          {/* Explicit conditionals (rendering-conditional-render) */}
          {identifierType !== null ? (
            // Active identifier mode: badge is the dropdown trigger
            <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1 text-xs font-medium transition-all duration-150"
                >
                  <span
                    className={twMerge(
                      'px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase border',
                      getIdentifierColors(identifierType),
                    )}
                  >
                    {identifierType}
                  </span>
                  <HiChevronDown
                    className={twMerge(
                      'w-3 h-3 text-primary-400 dark:text-primary-500 transition-transform duration-150',
                      dropdownOpen ? 'rotate-180' : '',
                    )}
                  />
                </button>
              </DropdownMenuTrigger>
              <SearchTypeDropdownContent
                identifierType={identifierType}
                onSelect={handleTypeSelect}
              />
            </DropdownMenu>
          ) : detectedType !== null && hasExactMatch ? (
            // Exact match confirmed: show solid badge (no "?"), still clickable to enter strict mode
            <button
              type="button"
              onClick={handleSwitchToDetectedType}
              className="group flex items-center gap-0.5 hover:opacity-80 transition-opacity"
            >
              <span
                className={twMerge(
                  'px-1 py-px rounded text-[8px] font-bold uppercase border',
                  getIdentifierColors(detectedType),
                )}
              >
                {detectedType}
              </span>
            </button>
          ) : (
            // Default: spinner when searching, filter icon when empty, magnifying glass when typing
            <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-0.5 text-primary-400 dark:text-primary-500 hover:text-primary-600 dark:hover:text-primary-300 transition-colors"
                >
                  {isSearching ? (
                    <Spinner className="w-[17px] h-[17px]" />
                  ) : showEmptyHint ? (
                    <HiOutlineFunnel className="w-[17px] h-[17px]" />
                  ) : (
                    <HiMagnifyingGlass className="w-[17px] h-[17px]" />
                  )}
                  <HiChevronDown
                    className={twMerge(
                      'w-3 h-3 transition-transform duration-150',
                      dropdownOpen ? 'rotate-180' : '',
                    )}
                  />
                </button>
              </DropdownMenuTrigger>
              <SearchTypeDropdownContent
                identifierType={identifierType}
                onSelect={handleTypeSelect}
              />
            </DropdownMenu>
          )}
        </div>

        {/* Input Field */}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          className={twMerge(
            'w-full py-2.5 text-sm rounded-lg transition-all duration-200',
            // Consistent padding - only changes when identifier mode is explicitly active
            // Detected type pill is compact enough to fit in the same space
            identifierType !== null ? 'pl-[72px]' : 'pl-[52px]',
            'pr-12',
            'bg-white dark:bg-primary-800',
            'text-primary-900 dark:text-primary-100',
            'placeholder:text-primary-400 dark:placeholder:text-primary-500',
            'border border-primary-300 dark:border-primary-600',
            'shadow-sm',
            'focus:outline-none focus:bg-white dark:focus:bg-primary-900',
            'focus:border-accent-500 dark:focus:border-accent-400',
            'focus:ring-2 focus:ring-accent-500/30 dark:focus:ring-accent-400/30',
          )}
        />

        {/* Clear Button */}
        <div className="absolute inset-y-0 right-0 flex items-center gap-1.5 pr-3">
          {inputValue || identifierType ? (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 rounded-md text-primary-400 hover:text-primary-600 hover:bg-primary-100 dark:text-primary-500 dark:hover:text-primary-300 dark:hover:bg-primary-700 transition-colors"
            >
              <HiXMark className="w-4 h-4" />
            </button>
          ) : null}
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto shrink-0">
        {/* Result Count */}
        {totalCount !== undefined ? (
          <div className="flex items-center gap-1">
            <span
              className={twMerge(
                'text-sm tabular-nums font-medium',
                isLoading
                  ? 'text-primary-400 dark:text-primary-500'
                  : 'text-primary-700 dark:text-primary-300',
              )}
            >
              {isLoading ? '...' : totalCount.toLocaleString()}
            </span>
            <span className="text-xs text-primary-400 dark:text-primary-500">
              {isLoading ? '' : totalCount === 1 ? 'result' : 'results'}
            </span>
          </div>
        ) : null}

        {/* Spacer on mobile to push sort/filter to right */}
        <div className="flex-1 sm:hidden" />

        {/* Separator */}
        <div className="hidden sm:block w-px h-5 bg-primary-200 dark:bg-primary-700" />

        {/* Sort Dropdown */}
        <Select value={filters.sort} onValueChange={handleSortChange}>
          <SelectTrigger className="w-[140px] sm:w-[155px] h-9 text-xs font-medium bg-white dark:bg-primary-800 border-primary-200 dark:border-primary-700 hover:border-primary-300 dark:hover:border-primary-600 transition-colors">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((option) => {
              const Icon = option.icon ? SORT_ICONS[option.icon] : null
              return (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  className="text-xs"
                >
                  <span className="flex flex-1 items-center gap-2 pointer-events-none">
                    {Icon ? (
                      <Icon className="w-3.5 h-3.5 text-primary-400 dark:text-primary-500" />
                    ) : null}
                    {option.label}
                  </span>
                </SelectItem>
              )
            })}
          </SelectContent>
        </Select>

        {/* Export Button */}
        <RecipientExportButton totalCount={totalCount} />

        {/* Filter Toggle (mobile) */}
        <Button
          variant="outline-compact"
          onClick={toggleFilterPanel}
          className={twMerge(
            'lg:hidden relative h-9 px-2.5',
            filterPanelOpen
              ? 'bg-accent-50 dark:bg-accent-900/30 border-accent-300 dark:border-accent-600 text-accent-700 dark:text-accent-300'
              : 'hover:bg-primary-50 dark:hover:bg-primary-800',
          )}
        >
          <HiOutlineAdjustmentsHorizontal className="w-4 h-4" />
          {activeFilterCount > 0 ? (
            <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-accent-600 rounded-full shadow-sm">
              {activeFilterCount}
            </span>
          ) : null}
        </Button>
      </div>
    </div>
  )
}

export default memo(RecipientToolbar)
